/**
 * Shared SMS opt-in legal disclaimer shown to customers on Redo marketing
 * signup forms. Single source of truth so the user-facing disclosure stays
 * in sync with the description we submit to Bandwidth during toll-free
 * verification.
 *
 * Bandwidth requires `optInWorkflow.description` to quote the exact
 * disclosure language shown to the consumer ("The exact disclosure
 * language shown to the consumer must be quoted here"), and mismatch
 * between the description and the opt-in screenshot is one of the most
 * common rejection reasons. Any edit to the customer-facing wording must
 * happen here so both surfaces update together.
 *
 * Renderers compose the disclaimer from text + link parts so React UI can
 * produce `ExternalLink` components while server-side code (e.g. the
 * Bandwidth submit-verification activity) can produce plain text. The
 * plaintext renderer drops the link URLs and keeps only the link text —
 * the URLs are not part of the "language shown to the consumer," only
 * the words are.
 *
 * TODO: the linked Terms of Service / Privacy Policy currently point to
 * Redo's pages. Per CTIA / carrier opt-in guidance these should be the
 * merchant's (they are the data controller; Redo is a service provider).
 * Switch to merchant URLs threaded through team context once we persist
 * them on the team document.
 */

export interface SmsLegalDisclaimerRenderers<T> {
  renderText: (text: string) => T;
  renderLink: (text: string, url: string) => T;
}

export const REDO_TERMS_OF_SERVICE_URL =
  "https://www.getredo.com/terms-conditions";
export const REDO_PRIVACY_POLICY_URL = "https://www.getredo.com/privacy-policy";

export interface SmsLegalDisclaimerOptions {
  /**
   * Brand name to use in place of the generic "this shop" wording. When
   * undefined (e.g. form-builder preview without a real team context) we
   * fall back to "this shop". Bandwidth requires the brand name to appear
   * in the disclosure, so this should always be populated in production.
   */
  brandName?: string;
  /**
   * Merchant support address, rendered inline: carriers reject a CTA whose
   * only support route is a link. Source from `team.customer_email`.
   */
  supportEmail?: string;
}

/** Registration is gated on this; a CTA without it is rejected. */
export function smsLegalDisclaimerHasSupportContact(
  options: SmsLegalDisclaimerOptions = {},
): boolean {
  return !!options.supportEmail?.trim();
}

export function smsLegalDisclaimerParts<T>(
  renderers: SmsLegalDisclaimerRenderers<T>,
  options: SmsLegalDisclaimerOptions = {},
): T[] {
  const { renderText, renderLink } = renderers;
  const sender = options.brandName?.trim() || "this shop";
  const supportEmail = options.supportEmail?.trim();
  const support = supportEmail
    ? `Email ${supportEmail} for support, or reply HELP for help and STOP to cancel.`
    : "Reply HELP for help and STOP to cancel.";
  return [
    renderText(
      `By providing your phone number, you agree to receive recurring automated marketing text messages (e.g. cart reminders) from ${sender}. Consent is not a condition to obtain goods or services. Msg & data rates may apply. Msg frequency varies. ${support} View `,
    ),
    renderLink("Terms of Service", REDO_TERMS_OF_SERVICE_URL),
    renderText(" and "),
    renderLink("Privacy Policy", REDO_PRIVACY_POLICY_URL),
    renderText("."),
  ];
}

/**
 * Plaintext form of the disclaimer suitable for quoting in API payloads
 * (e.g. Bandwidth `optInWorkflow.description`). Drops link URLs; keeps
 * only the link text since the URLs are not part of the consumer-visible
 * language Bandwidth wants quoted.
 */
export function smsLegalDisclaimerPlaintext(
  options: SmsLegalDisclaimerOptions = {},
): string {
  return smsLegalDisclaimerParts<string>(
    { renderText: (text) => text, renderLink: (text) => text },
    options,
  ).join("");
}
